/**
 * Redux slice for user management
 * Handles UI state: filters, pagination, dialog open/close states, selected user
 *
 * @module Features/UserManagement/slices
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * UI state for user management feature
 */
export interface UserManagementState {
  // Filter state
  filters: {
    searchEmail: string;
    role: string | null;
    isActive: boolean | null;
    emailConfirmed: boolean | null;
  };

  // Pagination state
  pagination: {
    page: number;
    pageSize: number;
  };

  // Dialog states
  dialogs: {
    createOpen: boolean;
    updateOpen: boolean;
    deleteOpen: boolean;
    resetPasswordOpen: boolean;
    detailsOpen: boolean;
  };

  // Selected user for dialogs
  selectedUserId: number | null;
}

/**
 * Initial state
 */
const initialState: UserManagementState = {
  filters: {
    searchEmail: '',
    role: null,
    isActive: null,
    emailConfirmed: null,
  },
  pagination: {
    page: 1,
    pageSize: 10,
  },
  dialogs: {
    createOpen: false,
    updateOpen: false,
    deleteOpen: false,
    resetPasswordOpen: false,
    detailsOpen: false,
  },
  selectedUserId: null,
};

/**
 * Redux slice for user management
 */
export const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    // Filter actions
    setSearchEmail: (state, action: PayloadAction<string>) => {
      state.filters.searchEmail = action.payload;
      state.pagination.page = 1; // Reset to first page when filter changes
    },

    setRoleFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.role = action.payload;
      state.pagination.page = 1;
    },

    setIsActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.filters.isActive = action.payload;
      state.pagination.page = 1;
    },

    setEmailConfirmedFilter: (state, action: PayloadAction<boolean | null>) => {
      state.filters.emailConfirmed = action.payload;
      state.pagination.page = 1;
    },

    /**
     * Clear all filters and reset to initial state
     */
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 1;
    },

    // Dialog actions
    openCreateDialog: (state) => {
      state.dialogs.createOpen = true;
    },

    closeCreateDialog: (state) => {
      state.dialogs.createOpen = false;
    },

    openUpdateDialog: (state, action: PayloadAction<number>) => {
      state.selectedUserId = action.payload;
      state.dialogs.updateOpen = true;
    },

    closeUpdateDialog: (state) => {
      state.dialogs.updateOpen = false;
      state.selectedUserId = null;
    },

    openDeleteDialog: (state, action: PayloadAction<number>) => {
      state.selectedUserId = action.payload;
      state.dialogs.deleteOpen = true;
    },

    closeDeleteDialog: (state) => {
      state.dialogs.deleteOpen = false;
      state.selectedUserId = null;
    },

    openResetPasswordDialog: (state, action: PayloadAction<number>) => {
      state.selectedUserId = action.payload;
      state.dialogs.resetPasswordOpen = true;
    },

    closeResetPasswordDialog: (state) => {
      state.dialogs.resetPasswordOpen = false;
      state.selectedUserId = null;
    },

    openDetailsDialog: (state, action: PayloadAction<number>) => {
      state.selectedUserId = action.payload;
      state.dialogs.detailsOpen = true;
    },

    closeDetailsDialog: (state) => {
      state.dialogs.detailsOpen = false;
      state.selectedUserId = null;
    },
  },
});

/**
 * Exported actions
 */
export const {
  setSearchEmail,
  setRoleFilter,
  setIsActiveFilter,
  setEmailConfirmedFilter,
  clearFilters,
  setPage,
  setPageSize,
  openCreateDialog,
  closeCreateDialog,
  openUpdateDialog,
  closeUpdateDialog,
  openDeleteDialog,
  closeDeleteDialog,
  openResetPasswordDialog,
  closeResetPasswordDialog,
  openDetailsDialog,
  closeDetailsDialog,
} = userManagementSlice.actions;

/**
 * Reducer
 */
export default userManagementSlice.reducer;
